import { DragEndEvent } from '@dnd-kit/core';
import { Canceler } from 'axios';
import { action, computed, makeObservable, observable } from 'mobx';
import { MealItemEditAction, defaultMeal, defaultMealSize, defaultMealValidation } from '../constants/meals';
import { IMealsValidation } from '../types/meals';
import { ITEM_TYPE, LOCALES, MEASURE, defaultPagination } from 'src/constants/main';
import { SCREENS } from 'src/constants/screens';
import { localeStore } from 'src/mobx/localesStore';
import client from 'src/services/client';
import { history } from 'src/services/history';
import { IMeal, IMealChoice, IMealGroup, IMealItem } from 'src/types/combo';
import { ICountsResponce, IPagination, TBooleanInNumber, TTranslate } from 'src/types/main';
import { IProductV4Size, PRODUCT_TYPE } from 'src/types/product';
import {
  booleanNumberReverse,
  countsUpdate,
  getAvailabilityParams,
  getCancelToken,
  getTranslate,
  randomFloatNumber,
  scrollToErrorElement,
} from 'src/utils/helpers';
import { emptyNameValidation, sameNameValidation, validateItemsOrderType } from 'src/utils/validation';
import { defaultItemsFilters } from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/constants/items';
import { IItemsFilters } from 'src/view/screens/MenuManagementScreen/screens/ItemsScreen/types/items';
import logger from '~/services/logger';
import { productUtils } from '~/utils/productUtils';
import { toastSuccess } from '~/utils/toasts';
import { convertMealId } from '~/view/screens/MenuManagementScreen/screens/MealsScreen/helpers/meals';

class MealsStore {
  constructor() {
    makeObservable(this);
  }

  cancelStack: {
    [key: string]: Canceler;
  } = {};

  @observable pagination: IPagination<IMeal> = defaultPagination;
  @observable meal: IMeal | null = null;
  @observable edited: boolean = false;
  @observable filters: IItemsFilters = defaultItemsFilters;
  @action setFilters = (filters: Partial<IItemsFilters>) => {
    this.filters = { ...this.filters, ...filters };
    this.pagination.current_page = 0;
    this.fetchMeals(1);
  };

  @computed get isLoading(): boolean {
    return Boolean(this.pagination.loading || this.pagination.refreshing);
  }

  @action fetchMeals = async (page: number = this.pagination.current_page + 1) => {
    try {
      const cancelId = 'fetchMeals';
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      const params: any = {
        page,
        name: this.filters.search,
        is_delete: 0,
        category_id: this.filters.category?.value || undefined,
        sort_field: this.filters.sort_field,
        sort_dir: this.filters.sort_dir,
        types: [PRODUCT_TYPE.MEAL_COMBO],
        ...getAvailabilityParams(this.filters.availability),
      };

      this.pagination.loading = true;
      this.pagination.refreshing = true;
      const pagination = await client.get('v4/products', {
        params,
        headers: { 'Accept-Language': 'en,ar' },
        cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)),
      });

      this.pagination = {
        ...pagination,
        loading: false,
        refreshing: false,
      };
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination = {
        ...this.pagination,
        loading: false,
        refreshing: false,
      };
      logger.error({ error, place: 'MealsStore fetchMeals' });
    }
  };

  public mealsCount: number | undefined = undefined;
  public foundMealsInSystem: boolean = false;
  public fetchMealsCount = async () => {
    try {
      const counts: ICountsResponce = await client.get(`v4/products_count`, { params: { types: [PRODUCT_TYPE.MEAL_COMBO] } });
      this.mealsCount = counts.available;
      this.foundMealsInSystem = Boolean(counts.available + counts.deleted);
    } catch (error: any) {
      logger.error({ error, place: 'MealsStore fetchMealsCount' });
    }
  };

  @action getMeal = async (id: string | number) => {
    try {
      this.pagination.loading = true;
      const meal: IMeal = await client.get(`v4/products/${id}`, { headers: { 'Accept-Language': 'en,ar' } });
      this.setMeal(meal && meal.id ? this.transformMeal(meal) : undefined);
      this.pagination.loading = false;
    } catch (error: any) {
      this.pagination.loading = false;
      logger.error({ error, place: 'MealsStore getMeal', showModal: true });
    }
  };

  @action setMeal = (meal?: IMeal) => {
    this.meal = meal ? this.transformMeal(meal) : this.defaultMeal;

    if (this.meal.images?.length) {
      this.images = [this.meal.image, ...this.meal.images];
    } else {
      this.images = this.meal.image ? [this.meal.image] : undefined;
    }

    // order type validate
    this.validateItemsOrderType();
  };
  private get defaultMealSize(): IProductV4Size {
    const count: string = String((this.meal?.sizes.length || 0) + 1);

    return {
      ...defaultMealSize,
      id: randomFloatNumber(),
      name: {
        en: getTranslate('txt_size', LOCALES.EN) + ' ' + count,
        ar: getTranslate('txt_size', LOCALES.AR) + ' ' + count,
      },
      weight: '0',
    };
  }
  private get defaultMealGroup(): IMealGroup {
    const count: string = String((this.meal?.groups.length || 0) + 1);

    return {
      id: randomFloatNumber(),
      name: {
        en: getTranslate('group', LOCALES.EN) + ' ' + count,
        ar: getTranslate('group', LOCALES.AR) + ' ' + count,
      },
    };
  }
  private get defaultMeal(): IMeal {
    const count: string = String((this.mealsCount ?? this.pagination.total) + 1);

    return {
      ...defaultMeal,
      name: {
        en: getTranslate('combo_meal', LOCALES.EN) + ' ' + count,
        ar: getTranslate('combo_meal', LOCALES.AR) + ' ' + count,
      },
      sizes: [this.defaultMealSize],
      groups: [this.defaultMealGroup],
    };
  }
  private transformMeal = (meal: IMeal): IMeal => {
    return {
      ...meal,
      external_id: meal.external_id?.toString(),
      price: meal.price.toString(),
      weight: meal.weight.toString(),
      unit: meal.unit || MEASURE.NULL,
      groups: meal.groups || [],
      sizes: meal.sizes || [],
      items: meal.items || [],
    };
  };

  // images block
  @observable images: string[] | undefined = undefined;
  @action uploadImage = (image: string) => {
    if (!this.images) this.images = [image];
    else this.images.push(image);
    this.edited = true;
  };
  @action deleteImage = (image: string) => {
    if (!this.images) return;

    this.images = this.images.filter((_image) => _image !== image);
    this.edited = true;
  };
  @action imagesChangesSubmit = (reset?: boolean) => {
    if (!this.meal) return;

    if (reset) {
      this.images = this.meal.is_draft ? undefined : [this.meal.image, ...(this.meal.images || [])];
    } else {
      return;
    }
  };
  // from DragAndDrop
  @observable draggableImage: string | null = null;
  @action setDraggableImage = (image: string | null) => {
    this.draggableImage = image ? image : null;
  };
  public handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!this.images) return;

    if (over && over.id !== 'image-overlay' && active.id !== over.id) {
      this.images = this.images.map((image) => {
        if (image === over.id) return active.id;
        if (image === active.id) return over.id;
        return image;
      });
      this.edited = true;
    }
    this.setDraggableImage(null);
  };

  // main details block
  @action changeMealName = (value: string, currentLanguage: LOCALES) => {
    if (!this.meal) return;

    this.meal.name = { ...this.meal.name, [currentLanguage]: value };
    this.edited = true;
    // update validation
    if (!this.validation.name.enValid || !this.validation.name.arValid) this.setValidation();
  };
  @action changeMealDescription = (value: string, currentLanguage: LOCALES) => {
    if (!this.meal) return;

    this.meal.description = { ...this.meal.description, [currentLanguage]: value };
    this.edited = true;
  };
  @action changeMealHandler = (value: Partial<IMeal>) => {
    if (!this.meal) return;

    this.meal = { ...this.meal, ...value };
    this.edited = true;
  };

  /**
   *
   * sizes block
   *
   */
  @action addSize = () => {
    if (!this.meal) return;
    const newSize: IProductV4Size = this.defaultMealSize;
    this.meal.sizes = this.meal.sizes.concat(newSize);
    this.edited = true;
    // update validation
    if (!this.validation.sizesLength) this.setValidation();
    //
    return newSize.id;
  };
  @action deleteSize = (id: number | undefined) => {
    if (!this.meal || !id) return;
    // delete size items
    this.meal.items.forEach((item) => {
      if (item.size_id === id) this.changeItemHandler(MealItemEditAction.DELETE, item);
    });
    this.meal.sizes = this.meal?.sizes.filter((size) => size.id !== id);
    this.edited = true;
  };
  @action renameSize = (id: number | undefined, newName: TTranslate) => {
    if (!this.meal || !id) return;
    this.meal.sizes = this.meal?.sizes.map((size) => {
      if (size.id === id) return { ...size, name: newName };
      return size;
    });
    this.edited = true;
  };

  /**
   *
   * groups block
   *
   */
  @action addGroup = () => {
    if (!this.meal) return;
    this.meal.groups = this.meal.groups.concat(this.defaultMealGroup);
    this.edited = true;
    // update validation
    if (!this.validation.groupsLength) this.setValidation();
  };
  @action deleteGroup = (id: number | undefined) => {
    if (!this.meal || !id) return;
    // delete group choices
    this.meal.choices.forEach((choice) => {
      if (choice.group_id === id) this.onDeleteChoice(choice.id);
    });
    this.meal.groups = this.meal?.groups.filter((group) => group.id !== id);
    this.edited = true;
  };
  @action renameGroup = (id: number | undefined, newName: TTranslate) => {
    if (!this.meal || !id) return;
    this.meal.groups = this.meal?.groups.map((group) => {
      if (group.id === id) return { ...group, name: newName };
      return group;
    });
    this.edited = true;
  };

  /**
   *
   * choices block
   *
   */
  @action public onEditChoice = (choice: IMealChoice, items: IMealItem[], edit?: boolean) => {
    if (!this.meal) return;

    this.meal.choices = edit ? this.meal.choices.map((_choice) => (_choice.id === choice.id ? choice : _choice)) : this.meal.choices.concat([choice]);

    // delete current choice items
    this.meal.items.forEach((item) => {
      if (item.choice_id === choice.id) this.changeItemHandler(MealItemEditAction.DELETE, item);
    });

    // add new choice items
    items.map((item) => this.changeItemHandler(MealItemEditAction.ADD, item));

    this.edited = true;
  };
  @action public onDeleteChoice = (choiceId: number) => {
    if (!this.meal) return;

    this.meal.choices = this.meal.choices.filter((_choice) => _choice.id !== choiceId);

    // delete choice items
    this.meal.items.forEach((item) => {
      if (item.choice_id === choiceId) this.changeItemHandler(MealItemEditAction.DELETE, item);
    });

    this.edited = true;
  };

  /**
   *
   * items block
   *
   */
  private setItems = (items: IMealItem[]) => {
    if (!this.meal) return;
    this.meal.items = items;
    this.edited = true;
    // update order type validation
    this.validateItemsOrderType();
    // update groups validation
    if (this.validation.groups.length) this.setValidation();
  };
  @action changeItemHandler = (changeType: MealItemEditAction, item?: IMealItem) => {
    if (!this.meal || !item) return;
    const otherItems: IMealItem[] = [];
    let relatedItems: IMealItem[] = [];

    this.meal.items.forEach((_item) => {
      if (productUtils.isSameComboItemsSizeGroup(item, _item)) {
        relatedItems.push(_item);
      } else {
        otherItems.push(_item);
      }
    });

    if (changeType === MealItemEditAction.ADD) {
      // if the element is only one, then we make it default
      if (!relatedItems.length) {
        this.setItems(otherItems.concat({ ...item, is_default: 1 }));
        return;
      }

      const isDefault: boolean = Boolean(item.is_default);
      // if item is default, then remove default from others
      if (isDefault) {
        relatedItems = relatedItems.concat(item);
        relatedItems = relatedItems.map((_item) => (productUtils.isSameComboItems(_item, item) ? item : { ..._item, is_default: 0 }));
        this.setItems(otherItems.concat(relatedItems));
        return;
      }
      // if the element is not only one and not default, then just add it
      this.setItems(this.meal.items.concat(item));
      return;
    }

    if (changeType === MealItemEditAction.EDIT) {
      // if the element is only one, then we make it default
      if (!relatedItems.length) {
        this.setItems(otherItems.concat({ ...item, is_default: 1 }));
        return;
      }

      // get default item, if there is one
      const defaultItem: IMealItem | undefined = relatedItems.find((_item) => _item.is_default);
      const isDefault: boolean = Boolean(item.is_default);
      // guaranteed to get an array without an item from same place in table
      relatedItems = relatedItems.filter((_item) => !productUtils.isSameComboItemsPlace(_item, item));
      // add element into array
      relatedItems = relatedItems.concat(item);
      // if item is default, then remove default from others
      if (isDefault) {
        relatedItems = relatedItems.map((_item) => (productUtils.isSameComboItems(_item, item) ? item : { ..._item, is_default: 0 }));
        this.setItems(otherItems.concat(relatedItems));
        return;
      } else {
        // if there was a default item, then item is in place, which is now default, we make it default,
        // otherwise we make the first item in the array default
        relatedItems = defaultItem
          ? relatedItems.map((_item) => (productUtils.isSameComboItemsPlace(_item, defaultItem) ? { ..._item, is_default: 1 } : _item))
          : relatedItems.map((_item, index) => (!index ? { ..._item, is_default: 1 } : _item));
      }

      this.setItems(otherItems.concat(relatedItems));
      return;
    }

    if (changeType === MealItemEditAction.DELETE) {
      // if the element is unique, then just remove it
      if (relatedItems.length === 1) {
        this.setItems(this.meal.items.filter((_item) => !productUtils.isSameComboItems(_item, item)));
        return;
      }

      // if the element is not only one, then delete it
      relatedItems = relatedItems.filter((_item) => !productUtils.isSameComboItems(_item, item));
      // if element was default, then set default to first one in array
      const isDefault: boolean = relatedItems.some((_item) => _item.is_default);
      if (!isDefault) relatedItems = relatedItems.map((_item, index) => (!index ? { ..._item, is_default: 1 } : _item));
      this.setItems(otherItems.concat(relatedItems));
      return;
    }
  };

  @action createMeal = async () => {
    try {
      if (!this.meal) return;

      this.pagination.loading = true;

      await client.post(`v4/products`, this.params);

      this.edited = false;
      history.push(SCREENS.MEALS());

      toastSuccess({ message: localeStore.t('toast_combo_meal_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.MEAL_COMBO, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'MealsStore createMeal', showModal: true });
    }
  };

  @action updateMeal = async (id: number) => {
    try {
      if (!this.meal) return;

      this.pagination.loading = true;

      await client.put(`v4/products/${id}`, this.params);

      this.edited = false;
      history.push(SCREENS.MEALS());

      toastSuccess({ message: localeStore.t('toast_combo_meal_changes_saved') });
    } catch (error: any) {
      this.pagination.loading = false;

      // name validation
      [this.validation.name, this.validation.valid] = sameNameValidation({ name: this.validation.name, type: ITEM_TYPE.MEAL_COMBO, error });
      if (!this.validation.valid) {
        scrollToErrorElement();
        return;
      }

      logger.error({ error, place: 'MealsStore updateMeal', showModal: true });
    }
  };
  private get params(): any {
    if (!this.meal) return {};

    return {
      ...this.meal,
      types: [PRODUCT_TYPE.MEAL_COMBO],
      id: this.meal.id || undefined,
      external_id: this.meal.external_id || undefined,
      category_id: this.meal.category?.id,
      description: this.meal.description ? this.meal.description : { en: null, ar: null },
      weight: this.meal.weight || 0,
      sort: Number(this.meal.sort) || 0,
      image: this.images?.length ? this.images[0] : null,
      images: this.images?.slice(1),
      //
      is_size: 1, // in meals always on
      sizes: this.meal.sizes.map((size, index) => {
        return { ...convertMealId(size), sort: index };
      }),
      groups: this.meal.groups.map((group) => convertMealId(group)),
      choices: this.meal.choices.map((choice) => convertMealId(choice)),
      items: this.meal.items.map((item) => convertMealId(item)),
      is_draft: undefined,
    };
  }

  @action changeStock = async (id: number, stock: TBooleanInNumber) => {
    try {
      const cancelId = `stock_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');
      stock = booleanNumberReverse(stock);

      this.pagination.data = this.pagination.data.map((meal) => (meal.id === id ? { ...meal, out_of_stock: stock } : meal));

      await client.put(
        `v4/products/${id}`,
        { out_of_stock: stock, apply_availability_to_all: 1 },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      toastSuccess({ message: localeStore.t('toast_combo_meal_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((meal) =>
        meal.id === id ? { ...meal, out_of_stock: booleanNumberReverse(meal.out_of_stock) } : meal
      );
      logger.error({ error, place: 'MealsStore changeStock' });
    }
  };

  @action changeStatus = async (id: number, status: TBooleanInNumber) => {
    try {
      const cancelId = `status_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.data = this.pagination.data.map((meal) => (meal.id === id ? { ...meal, status } : meal));

      await client.put(
        `v4/products/${id}`,
        { status, apply_availability_to_all: 1 },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      toastSuccess({ message: localeStore.t('toast_combo_meal_changes_saved') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.data = this.pagination.data.map((meal) => (meal.id === id ? { ...meal, status: booleanNumberReverse(meal.status) } : meal));
      logger.error({ error, place: 'MealsStore changeStatus' });
    }
  };

  @action restoreMeal = async (id: number) => {
    try {
      const cancelId = `restore_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.patch(
        `v4/products/${id}`,
        { type: PRODUCT_TYPE.MEAL_COMBO },
        { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) }
      );

      this.fetchMeals(this.pagination.current_page);
      this.mealsCount = countsUpdate(this.mealsCount, true);

      toastSuccess({ message: localeStore.t('toast_combo_meal_restored') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'MealsStore restoreMeal', showModal: true });
    }
  };

  @action deleteMeal = async (id: number) => {
    try {
      const cancelId = `delete_${id}`;
      if (this.cancelStack[cancelId]) this.cancelStack[cancelId]('Cancelled');

      this.pagination.refreshing = true;

      await client.delete(`v4/products/${id}`, { cancelToken: getCancelToken((c) => (this.cancelStack[cancelId] = c)) });

      this.fetchMeals(this.pagination.current_page);
      this.mealsCount = countsUpdate(this.mealsCount);

      toastSuccess({ message: localeStore.t('toast_combo_meal_deleted') });
    } catch (error: any) {
      if (error.message === 'Cancelled') return;
      this.pagination.refreshing = false;
      logger.error({ error, place: 'MealsStore deleteMeal', showModal: true });
    }
  };

  @observable isConflictedItemsOrderType: boolean = false;
  @action validateItemsOrderType = () => {
    const { items } = this.meal || {};
    if (!items) return;

    this.isConflictedItemsOrderType = validateItemsOrderType(items);
  };

  @observable validation: IMealsValidation = defaultMealValidation;
  @action setValidation = () => {
    if (!this.meal) return;

    this.validation = { ...defaultMealValidation };

    // images validation
    // if (!this.images?.length) {
    //   this.validation.images = false;
    //   this.validation.valid = false;
    // }

    // name validation
    [this.validation.name, this.validation.valid] = emptyNameValidation(this.meal.name, this.validation.name, this.validation.valid);

    // category validation
    if (!this.meal.category) {
      this.validation.valid = false;
      this.validation.category = false;
      this.validation.categoryErrorText = localeStore.t('required_field_error');
    }

    // sizes length validation
    if (!this.meal.sizes.length) {
      this.validation.valid = false;
      this.validation.sizesLength = false;
    }

    // groups length validation
    if (!this.meal.groups.length) {
      this.validation.valid = false;
      this.validation.groupsLength = false;
    }

    // group items length validation
    // if (this.meal.groups.length) {
    //   this.validation.groups = this.meal.groups.map((group) => {
    //     const _group: IMealsGroupValidation = { id: group.id, itemsLength: true };

    //     if (!this.meal?.items.some((item) => item.group_id === group.id)) {
    //       this.validation.valid = false;
    //       _group.itemsLength = false;
    //     }

    //     return _group;
    //   });
    // }
  };

  @action unMount = () => {
    this.meal = null;
    this.images = undefined;
    this.edited = false;
    this.validation = defaultMealValidation;

    if (!window.location.pathname.includes(SCREENS.MEALS())) this.filters = defaultItemsFilters;
  };
}

const mealsStore = new MealsStore();

export default mealsStore;
